import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { cva, type VariantProps } from 'class-variance-authority';

const cardVariants = cva(
  'relative h-fit w-fit rounded-lg bg-ui-primary aria-disabled:pointer-events-none aria-disabled:border-none aria-disabled:bg-ui-secondary aria-disabled:shadow-none',
  {
    variants: {
      variant: {
        default:
          'shadow-[0px_0px_5px_0px_rgba(23,24,24,0.05),0px_1px_2px_0px_rgba(0,0,0,0.15)]',
        outlined: 'border shadow-none',
      },
      color: {
        default: null,
        warning: 'border border-warning-main',
        error: 'border border-error-main',
        ai: 'border-double border-transparent-main [background-image:linear-gradient(var(--color-ui-primary),var(--color-ui-primary)),linear-gradient(to_top_right,var(--color-ai-lightCyan),var(--color-ai-lightGreen))] [background-clip:content-box,border-box] shadow-none',
      },
      selected: {
        true: 'bg-background-tertiary',
        false: 'bg-ui-primary',
      },
      hover: {
        noHover:
          'hover:shadow-[0px_0px_5px_0px_rgba(23,24,24,0.05),0px_1px_2px_0px_rgba(0,0,0,0.15)]',
        hoverSmall:
          'hover:shadow-[0px_2px_4px_-2px_rgba(16,24,40,0.06),0px_4px_8px_-2px_rgba(16,24,40,0.10)]',
        hoverMedium: 'hover:shadow-[0px_10px_20px_-8px_rgba(16,24,40,0.20)]',
        hoverBig: 'hover:shadow-[0px_15px_15px_-8px_rgba(16,24,40,0.15)]',
      },
    },

    compoundVariants: [
      {
        hover: ['noHover', 'hoverSmall', 'hoverMedium', 'hoverBig'],
        variant: 'outlined',
        className: 'hover:shadow-none',
      },
      {
        color: 'default',
        variant: 'outlined',
        className: 'border-ui-tertiary',
      },
      {
        color: 'default',
        variant: 'outlined',
        selected: true,
        className: 'border-tertiary-main',
      },
      {
        color: 'default',
        variant: 'default',
        selected: true,
        className: 'border border-tertiary-main',
      },
      {
        color: 'ai',
        variant: 'default',
        className:
          'border before:inset-0 before:z-[-1] before:rounded-md before:bg-linear-to-tr before:from-ai-lightCyan before:to-ai-lightGreen before:opacity-0 before:blur before:transition-opacity before:duration-200 not-only-of-type:before:absolute hover:before:opacity-50',
      },
    ],
    defaultVariants: {
      variant: 'default',
      color: 'default',
      selected: false,
      hover: 'hoverSmall',
    },
  },
);
function Card({
  className,
  contentClassName,
  variant,
  color,
  selected,
  disabled,
  hover,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants> & {
    disabled?: boolean;
    contentClassName?: string;
  }) {
  return (
    <div
      data-slot="card"
      aria-disabled={disabled}
      className={cn(
        cardVariants({ variant, color, selected, hover }),
        className,
      )}
      {...props}
    >
      <CardContent className={cn(contentClassName)}>
        {props.children}
      </CardContent>
    </div>
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-content" className={cn('p-3', className)} {...props} />
  );
}

export { Card };
